/**
 * schritte.js — die ABLEITUNG „was fehlt diesem Fall noch", rein und ohne DOM.
 *
 * @description
 *   Die Liste und das Detail brauchen dieselbe Antwort auf dieselbe Frage: „welcher
 *   Schritt fehlt, und warum?" Bis zum Umbau stand sie verteilt in `OpenCases.jsx`
 *   (`zeilenLage`) und in den Server-Absagen von `approve`/`lege_listing_an`. Diese
 *   Datei ist die EINE Quelle: `schritte(zeile, vorschau)` liefert vier Schritte, je
 *   mit `lage ∈ 'erledigt' | 'offen' | 'blockiert' | 'wartet'`. Die Liste zeigt
 *   `kurz`, das Detail zeigt `grund`/`hinweis`/`weg` am Schritt.
 *
 * @useWhen
 *   - Ein Fall soll zeigen, welcher Schritt als Nächstes dran ist (Liste und Detail)
 *   - Eine Oberfläche braucht den GRUND einer Blockade, ohne den Server zu fragen
 *
 * @dontUseFor
 *   - Server-Wahrheiten (Bestand, Kanal, Live-Preis beim Klick) — die prüft `approve`
 *   - Eine zweite Fassung der Rabatt-Regel: die EK-Grenze steht in `rabatt.js::ekBekannt`
 *     (JS) UND in `second_life_rabatt.py::pruefe_rabatt` (Python), zwei Sprachen,
 *     zwei Laufzeiten — die Kopplungsprobe in `schritte.test.js`/pytest hält sie zusammen
 *
 * @related
 *   - `bestand.js` — `rabattMoeglich`, `beschreibungGrund`, `BESCHREIBUNG_LABEL`
 *   - `rabatt.js` — `ekBekannt` (die EK-Grenze, hier wiederverwendet)
 *   - `Vorschlagsteil.jsx` / `FallDetail.jsx` — die zwei Darstellungen
 *
 * @reuseHints
 *   - Eine spätere „Was blockiert am meisten?"-Auswertung über alle Fälle
 *   - Ein Filter „nur blockierte / nur bereite" in der Listen-Kopfzeile (S12)
 *
 * @props (Parameter)
 *   - zeile    Eine Zeile des Bestandsvertrags (`second_life_bestand._bau`)
 *   - vorschau Die Antwort von `GET /api/second-life/vorschau` oder `null`.
 *              `null` = Liste (kein Live-Preis); gesetzt = Detail (Live-Preis ist da)
 */

import { rabattMoeglich, beschreibungGrund } from './bestand';
import { ekBekannt, alsCent } from './rabatt';

/** Die vier Schritte in der Reihenfolge, in der sie aufeinander aufbauen. */
export const SCHRITT_KEYS = ['rabatt', 'text', 'entwurf', 'freigabe'];

/** Die Titel je Schritt — eine Quelle für Liste und Detail. */
export const SCHRITT_TITEL = {
  rabatt: 'Confirm the discount',
  text: 'Text for the product page',
  entwurf: 'Create the Shopify draft',
  freigabe: 'Release',
};

/**
 * Die Gründe, die VOR dem Klick feststehen — als Token, nicht als Satz.
 *
 * 🔴 **Reihenfolge = Aussage** (§3.2 des Plans): die spezifische vor der
 * allgemeinen, damit der Satz etwas sagt. `keine_aggregat_zeile` → `ek_fehlt` →
 * `a_vk_fehlt` → `kein_weg`.
 */
export const GRUND_KEINE_AGGREGAT_ZEILE = 'keine_aggregat_zeile';
export const GRUND_EK_FEHLT = 'ek_fehlt';
export const GRUND_A_VK_FEHLT = 'a_vk_fehlt';
export const GRUND_KEIN_WEG = 'kein_weg';

/** Der Satz je Grund, der am Schritt steht. EINE Quelle je Token. */
export const GRUND_TEXT = {
  [GRUND_KEINE_AGGREGAT_ZEILE]:
    'This row is a single unit — the discount belongs to the aggregate row.',
  [GRUND_EK_FEHLT]:
    'Set the cost price in the base product — the discount cannot be confirmed without it.',
  [GRUND_A_VK_FEHLT]:
    'The base product has no sale price — set it in the base product first.',
  [GRUND_KEIN_WEG]:
    'This row cannot take a discount yet.',
};

/** Der kurze Text für die Liste („Next: …") je Grund. */
export const GRUND_KURZ = {
  [GRUND_KEINE_AGGREGAT_ZEILE]: 'Next: use the aggregate row',
  [GRUND_EK_FEHLT]: 'Next: cost price missing',
  [GRUND_A_VK_FEHLT]: 'Next: A price missing',
  [GRUND_KEIN_WEG]: 'Next: no discount path',
};

/**
 * Der Grund des Rabatt-Schritts — oder `null`, wenn er bedienbar ist.
 *
 * 🔴 **Zwei Quellen für den A-Preis, und sie sind NICHT gleich** (§4.3):
 *   - `vorschau.a_vk_fehlt` ist der LIVE-Preis → **Sperre** (das Detail)
 *   - `zeile.vorschlag.a_vk == null` ist der Sync-Stand → nur **Hinweis** (die Liste)
 * Ein Sync-Stand, der in beide Richtungen falsch sein kann, darf keinen Schritt
 * blockieren.
 */
export function rabattGrund(zeile, vorschau) {
  if (!zeile) return null;
  const ohneKladde = zeile.id == null;
  if (!ohneKladde && zeile.kind !== 'aggregate') return GRUND_KEINE_AGGREGAT_ZEILE;
  // EK: verbindlich, aus derselben Quelle wie der Server (`pruefe_rabatt`).
  const ek = vorschau ? vorschau.ek : zeile.vorschlag?.ek;
  if (!ekBekannt(alsCent(ek))) return GRUND_EK_FEHLT;
  // A-Preis: LIVE ist Sperre, Sync ist Hinweis (s. Docstring).
  if (vorschau && vorschau.a_vk_fehlt === true) return GRUND_A_VK_FEHLT;
  // Kein Rabattweg für diese Zeile (fail-closed, `rabattMoeglich`).
  if (!rabattMoeglich(zeile)) return GRUND_KEIN_WEG;
  return null;
}

/** Ist der Entwurfstext da? Leer zählt als fehlend. */
const hatText = (zeile) => {
  const t = zeile?.desc_text;
  return typeof t === 'string' && t.trim() !== '';
};

/**
 * Die vier Schritte eines Falls.
 *
 * @returns {Array<{key: string, titel: string, lage: string, grund: ?string,
 *                  hinweis: ?string, weg: ?string, kurz: ?string}>}
 */
export function schritte(zeile, vorschau = null) {
  if (!zeile) return [];

  // ── rabatt ────────────────────────────────────────────────────────────────
  const rGrund = rabattGrund(zeile, vorschau);
  const rabattErledigt = zeile.rabatt_pct != null;
  // Der Sync-Hinweis: A-Preis fehlt im Sync-Stand, aber LIVE (noch) nicht geprüft.
  const aVkHinweis = !vorschau && zeile.vorschlag?.a_vk == null;
  const rabattLage = rabattErledigt
    ? 'erledigt'
    : (rGrund ? 'blockiert' : 'offen');
  const rabatt = {
    key: 'rabatt',
    titel: SCHRITT_TITEL.rabatt,
    lage: rabattLage,
    grund: rGrund ? GRUND_TEXT[rGrund] : null,
    grundToken: rGrund,
    hinweis: aVkHinweis ? 'The A price is missing in the last sync.' : null,
    weg: rGrund === GRUND_EK_FEHLT || rGrund === GRUND_A_VK_FEHLT ? 'base_product' : null,
    kurz: rabattErledigt
      ? null
      : (rGrund ? GRUND_KURZ[rGrund] : 'Next: set the discount'),
  };

  // ── text ──────────────────────────────────────────────────────────────────
  const textErledigt = hatText(zeile);
  const textLage = textErledigt
    ? 'erledigt'
    : (zeile.state === 'none' ? 'wartet' : 'offen');
  const text = {
    key: 'text',
    titel: SCHRITT_TITEL.text,
    lage: textLage,
    grund: null,
    grundToken: null,
    // 🔴 **Der Hinweis hängt an der LAGE, nicht am Zustand** (R15-B3 / R16-C1):
    // ein erledigter Schritt erklärte sich sonst mit dem Satz für den offenen.
    hinweis: textLage === 'wartet'
      ? 'Written when the draft is created — the model suggests it from the LSM check notes.'
      : null,
    weg: null,
    kurz: textErledigt
      ? null
      : (zeile.state === 'none' ? 'No draft yet' : 'Next: write the text'),
  };

  // ── entwurf ───────────────────────────────────────────────────────────────
  const entwurfErledigt = zeile.state === 'draft' && zeile.hat_produkt === true;
  const entwurfLage = entwurfErledigt
    ? 'erledigt'
    : (rabattLage !== 'erledigt' ? 'wartet' : 'offen');
  const entwurf = {
    key: 'entwurf',
    titel: SCHRITT_TITEL.entwurf,
    lage: entwurfLage,
    grund: null,
    grundToken: null,
    hinweis: entwurfLage === 'wartet' ? 'Waiting for the discount.' : null,
    weg: null,
    kurz: entwurfErledigt
      ? null
      : (entwurfLage === 'wartet' ? 'Next: confirm the discount' : 'Next: create the draft'),
  };

  // ── freigabe ──────────────────────────────────────────────────────────────
  // 🔴 Was hier NICHT vorab geprüft wird: `kein_bestand`, `kein_kanal`,
  // `kein_text` (live) und `achsen_unlesbar` sind Netz-/Live-Prüfungen; der
  // Server prüft sie beim Klick. Eine Checkliste, die einen Haken setzt, den
  // nur der Server setzen kann, wäre eine zweite Quelle für dieselbe Aussage.
  // 🔴 **Ohne Entwurf WARTET die Freigabe** (R15-B2 / R16-B2). Hier stand
  // `textLage !== 'erledigt' ? 'blockiert'` — im Zustand `none` ist der Text
  // aber nicht „fehlend", sondern „noch nicht dran", und die Fläche zeigte
  // ZWEI rote Blockaden für EINEN echten Grund. `blockiert` gibt es nur noch,
  // wenn der Entwurf da ist und sein Text fehlt.
  const freigabeLage = zeile.state !== 'draft'
    ? 'wartet'
    : (textLage !== 'erledigt' ? 'blockiert' : 'offen');
  const freigabe = {
    key: 'freigabe',
    titel: SCHRITT_TITEL.freigabe,
    lage: freigabeLage,
    grund: freigabeLage === 'blockiert' ? 'The product page text is missing.' : null,
    grundToken: freigabeLage === 'blockiert' ? 'text_fehlt' : null,
    hinweis: freigabeLage === 'wartet'
      ? (zeile.state === 'none'
        ? 'Waiting for the draft.'
        : 'The draft is read live when you release.')
      : null,
    weg: null,
    // 🔴 **R15-B1 / R16-B1:** hier stand für jede nicht-erledigte Lage
    // „Next: create the draft" — auch für den freigabebereiten Entwurf, den
    // die Liste unter „Draft ready to release" führt. Das abgenommene Mockup
    // (Konzept B, Zeile „Mühle M-2") sagt „Next: release".
    kurz: freigabeLage === 'blockiert'
      ? 'Next: write the text'
      : (freigabeLage === 'wartet' ? 'Next: create the draft' : 'Next: release'),
  };

  return [rabatt, text, entwurf, freigabe];
}

/**
 * Der erste Schritt, der HANDELN lässt (Lage `offen`) — oder `null`.
 *
 * 🔴 **Nicht „der erste, der nicht erledigt ist"** (Grill W3): ist der erste
 * offene Schritt `blockiert`, gibt es dort keine Handlung, und ein gesperrter
 * Knopf wäre genau der tote Knopf, den Konzept A abgeschafft hat.
 */
export function ersterOffenerSchritt(zeile, vorschau = null) {
  return schritte(zeile, vorschau).find((s) => s.lage === 'offen') || null;
}

/**
 * Der Grund der Description-Zelle, wenn kein Entwurf da ist (UX-Punkt 1).
 *
 * 🔴 **Der Grund, nicht der Zustand noch einmal** (R16 „Volle Breite", Doppelter
 * Nullfall; R15-W4). Die Pille daneben sagt den Zustand („Missing"), diese
 * Zeile sagt WARUM. Vorher stand hier `textKurz` = `'No draft yet'` — dieselbe
 * Aussage ein zweites Mal — und für eine veröffentlichte Zeile sogar
 * „Next: write the text", obwohl die Beschreibung dort gesperrt ist.
 */
export function textKurz(zeile) {
  if (!zeile) return null;
  const s = schritte(zeile, null).find((x) => x.key === 'text');
  if (!s || s.lage === 'erledigt') return null;
  if (s.lage === 'wartet') return 'Written when the draft is created';
  const sperre = beschreibungGrund(zeile);
  if (sperre) return sperre;
  return 'The draft has no text yet';
}
